// src/pages/BlogDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    Calendar,
    // User, - Removed as it's unused
    Tag,
    Clock,
    ChevronLeft,
    MessageCircle,
    // Share2, - Removed as it's unused
    Bookmark,
    Facebook,
    Twitter,
    Linkedin,
    Mail,
    Heart,
    Copy,
    ChevronRight,
    // Add the missing icons
    Coffee,
    Globe,
    TrendingUp
} from 'lucide-react';
import '../styles/blog-detail.css';

const BlogDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [comment, setComment] = useState('');
    const [comments, setComments] = useState([]);
    const [liked, setLiked] = useState(false);
    const [bookmarked, setBookmarked] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        // Scroll to top when page loads
        window.scrollTo(0, 0);

        // Simulate API fetch for blog post
        setIsLoading(true);

        setTimeout(() => {
            const allPosts = getBlogPosts();
            const featuredPosts = allPosts.featured;
            const regularPosts = allPosts.regular;
            const allPostsCombined = [...featuredPosts, ...regularPosts];

            const currentPost = allPostsCombined.find(post => post.id === parseInt(id));

            if (currentPost) {
                setPost(currentPost);

                // Find related posts (same category)
                const related = allPostsCombined
                    .filter(p => p.category === currentPost.category && p.id !== currentPost.id)
                    .slice(0, 3);

                setRelatedPosts(related);

                // Simulate comments
                setComments(getComments(currentPost.id));
            } else {
                // Handle post not found
                navigate('/blog', { replace: true });
            }

            setIsLoading(false);
        }, 800);
    }, [id, navigate]);

    // Sample blog post data
    const getBlogPosts = () => {
        return {
            featured: [
                {
                    id: 1,
                    title: "The Rise of Halal Dining in Major US Cities",
                    excerpt: "Exploring the growing popularity of halal restaurants across American metropolitan areas and what's driving this culinary trend.",
                    category: "industry-news",
                    author: "Sarah Johnson",
                    authorRole: "Food Trend Analyst",
                    authorImage: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&w=100",
                    date: "February 15, 2025",
                    readTime: 6,
                    image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2074",
                    comments: 24,
                    featured: true,
                    content: `
            <p>The landscape of American dining is undergoing a significant transformation as halal restaurants are making their mark in major cities across the United States. This culinary revolution isn't just reshaping food choices but reflecting broader cultural shifts in American society.</p>
            
            <h2>What's Behind the Trend?</h2>
            
            <p>Several factors are driving this remarkable growth in halal dining options:</p>
            
            <ul>
              <li><strong>Demographic Changes:</strong> The Muslim population in the United States has been steadily increasing, creating a natural market for halal food options.</li>
              <li><strong>Cultural Curiosity:</strong> Non-Muslim Americans are increasingly exploring diverse cuisines and cultural experiences.</li>
              <li><strong>Health Perceptions:</strong> Many consumers perceive halal meat as a higher quality, more ethically produced option.</li>
              <li><strong>Tech-Enabled Discovery:</strong> Apps and platforms dedicated to finding halal dining options have made these restaurants more accessible.</li>
            </ul>
            
            <p>According to recent market research, the halal food sector in the United States is experiencing annual growth rates of approximately 15%, significantly outpacing the overall restaurant industry.</p>
            
            <blockquote>
              "We've seen reservation requests for halal restaurants increase by 67% over the past two years. This isn't just a passing trend—it's a fundamental shift in dining preferences." — Restaurant Booking Platform Report, 2024
            </blockquote>
            
            <h2>From Food Trucks to Fine Dining</h2>
            
            <p>What's particularly notable about this evolution is the diversification of halal dining options. No longer confined to kebab shops and food trucks, halal restaurants now span the culinary spectrum:</p>
            
            <ul>
              <li>Upscale halal steakhouses in Manhattan</li>
              <li>Halal fusion cuisine in Los Angeles</li>
              <li>Halal-certified farm-to-table restaurants in Chicago</li>
              <li>Gourmet halal burger joints in Houston</li>
              <li>Halal omakase experiences in San Francisco</li>
            </ul>
            
            <p>This diversification reflects a maturing market and signals that halal dining has firmly established itself within the American culinary mainstream.</p>
            
            <h2>The Business Opportunity</h2>
            
            <p>For restaurant entrepreneurs, this trend represents a significant business opportunity. Certification processes have become more streamlined, and consumer demand continues to grow. Most importantly, halal restaurants are increasingly attracting diverse clientele beyond their core demographic.</p>
            
            <p>As we look toward the future, it's clear that halal dining will continue to flourish and evolve. The restaurants that thrive will be those that not only adhere to halal standards but also embrace innovation, quality, and exceptional dining experiences.</p>
          `
                },
                {
                    id: 2,
                    title: "10 Must-Visit Halal Restaurants in New York City",
                    excerpt: "From hidden gems to famous establishments, discover the best halal dining experiences the Big Apple has to offer.",
                    category: "halal-guides",
                    author: "Mohammed Ali",
                    authorRole: "Food Critic & Travel Writer",
                    authorImage: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&w=100",
                    date: "February 10, 2025",
                    readTime: 8,
                    image: "https://images.unsplash.com/photo-1518684079-3c830dcef090?q=80&w=2070",
                    comments: 36,
                    featured: true,
                    content: `
            <p>New York City's diverse culinary landscape is home to some of the most exceptional halal dining experiences in the world. From traditional Middle Eastern cuisine to innovative fusion concepts, the city offers halal food enthusiasts an incredible array of options.</p>
            
            <p>Here's our carefully curated list of the 10 must-visit halal restaurants in the Big Apple:</p>
            
            <h2>1. The Halal Guys - Midtown</h2>
            
            <p>No list would be complete without mentioning this iconic NYC institution. What began as a humble food cart is now a global phenomenon. Their chicken and rice platter with the famous white sauce remains the gold standard for halal street food.</p>
            
            <p><strong>Neighborhood:</strong> Midtown<br>
            <strong>Price Range:</strong> $<br>
            <strong>Specialty:</strong> Chicken and rice platter</p>
            
            <h2>2. Adda Indian Canteen - Long Island City</h2>
            
            <p>This critically acclaimed restaurant serves authentic, regional Indian cuisine in a casual setting. Chef Chintan Pandya's bold, unapologetically authentic approach has earned Adda numerous accolades.</p>
            
            <p><strong>Neighborhood:</strong> Long Island City, Queens<br>
            <strong>Price Range:</strong> $$<br>
            <strong>Specialty:</strong> Goat Biryani</p>
            
            <h2>3. Tanoreen - Bay Ridge</h2>
            
            <p>Chef Rawia Bishara's Tanoreen has been serving exceptional Middle Eastern cuisine for over 20 years. The menu showcases Palestinian dishes with Mediterranean influences.</p>
            
            <p><strong>Neighborhood:</strong> Bay Ridge, Brooklyn<br>
            <strong>Price Range:</strong> $$$<br>
            <strong>Specialty:</strong> Knafeh and Mansaf</p>
            
            <h2>4. Nur - Flatiron District</h2>
            
            <p>This modern Middle Eastern restaurant offers a sophisticated take on traditional dishes. Chef Meir Adoni combines his Israeli heritage with global culinary techniques.</p>
            
            <p><strong>Neighborhood:</strong> Flatiron District<br>
            <strong>Price Range:</strong> $$$<br>
            <strong>Specialty:</strong> Jerusalem sesame bagel</p>
            
            <h2>5. BK Jani - Williamsburg</h2>
            
            <p>This Pakistani street food-inspired spot is known for its exceptional burgers and kebabs. The minimalist menu focuses on quality over quantity.</p>
            
            <p><strong>Neighborhood:</strong> Williamsburg, Brooklyn<br>
            <strong>Price Range:</strong> $$<br>
            <strong>Specialty:</strong> Chapli kebab burger</p>
            
            <h2>6. Ravagh Persian Grill - Upper East Side</h2>
            
            <p>A long-standing favorite for Persian cuisine, Ravagh offers exceptionally tender kebabs and aromatic rice dishes in a refined setting.</p>
            
            <p><strong>Neighborhood:</strong> Upper East Side<br>
            <strong>Price Range:</strong> $$$<br>
            <strong>Specialty:</strong> Koobideh kebab</p>
            
            <h2>7. Ayat - Bay Ridge</h2>
            
            <p>This Palestinian restaurant showcases home-style cooking with exceptional attention to detail. Their mansaf and musakhan are widely considered the best in the city.</p>
            
            <p><strong>Neighborhood:</strong> Bay Ridge, Brooklyn<br>
            <strong>Price Range:</strong> $$<br>
            <strong>Specialty:</strong> Musakhan (sumac chicken)</p>
            
            <h2>8. Beyti Turkish Kebab - Midwood</h2>
            
            <p>This unassuming restaurant serves authentic Turkish cuisine with a focus on grilled meats and traditional dishes.</p>
            
            <p><strong>Neighborhood:</strong> Midwood, Brooklyn<br>
            <strong>Price Range:</strong> $$<br>
            <strong>Specialty:</strong> Adana kebab</p>
            
            <h2>9. Savor Halal - Astoria</h2>
            
            <p>A relatively new addition to NYC's halal scene, Savor Halal offers innovative fusion cuisine that combines South Asian flavors with American classics.</p>
            
            <p><strong>Neighborhood:</strong> Astoria, Queens<br>
            <strong>Price Range:</strong> $$<br>
            <strong>Specialty:</strong> Butter chicken mac and cheese</p>
            
            <h2>10. Yemen Café - Brooklyn Heights</h2>
            
            <p>This family-owned restaurant serves authentic Yemeni cuisine, including the famous salta and fahsa stews, along with freshly baked flatbread.</p>
            
            <p><strong>Neighborhood:</strong> Brooklyn Heights<br>
            <strong>Price Range:</strong> $$<br>
            <strong>Specialty:</strong> Lamb haneeth</p>
            
            <p>Whether you're a visitor exploring the city or a local looking to expand your culinary horizons, these restaurants offer a glimpse into the incredible diversity and quality of halal dining in New York City.</p>
          `
                },
                {
                    id: 3,
                    title: "How Al-Madina Grill Transformed with Technology",
                    excerpt: "A success story of how one family-owned restaurant embraced digital tools to grow their business and customer base.",
                    category: "restaurant-spotlights",
                    author: "Fatima Khan",
                    authorRole: "Tech & Restaurant Consultant",
                    authorImage: "https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&w=100",
                    date: "February 5, 2025",
                    readTime: 5,
                    image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070",
                    comments: 19,
                    featured: true,
                    content: `
            <p>When brothers Sami and Omar Haddad took over their father's struggling restaurant, Al-Madina Grill, in 2023, they faced a critical decision: maintain tradition at the risk of closure or embrace change to ensure survival. Their journey of transformation offers valuable lessons for family-owned restaurants navigating the modern dining landscape.</p>
            
            <h2>The Challenge</h2>
            
            <p>Al-Madina Grill had been a neighborhood fixture in Chicago's Albany Park for over 20 years, known for authentic Lebanese cuisine and warm hospitality. Despite loyal customers, the restaurant faced increasing challenges:</p>
            
            <ul>
              <li>Declining foot traffic</li>
              <li>No online presence or ordering system</li>
              <li>Inefficient inventory management</li>
              <li>Limited visibility to new customers</li>
              <li>Rising operational costs</li>
            </ul>
            
            <p>"We were proud of our food and service, but we were invisible to anyone outside our immediate neighborhood," explains Sami Haddad. "The pandemic had changed dining habits permanently, and we hadn't adapted."</p>
            
            <h2>The Digital Transformation</h2>
            
            <p>The brothers developed a comprehensive technology strategy with several key components:</p>
            
            <h3>1. Digital Presence Overhaul</h3>
            
            <p>They invested in professional photography showcasing their dishes and created a modern, mobile-friendly website featuring their menu, story, and online ordering capabilities. They also established profiles on major review platforms and social media channels.</p>
            
            <h3>2. Streamlined Operations</h3>
            
            <p>The team implemented a cloud-based POS system that integrated online orders, inventory management, and customer data. This eliminated duplicate work and provided valuable insights into customer preferences and ordering patterns.</p>
            
            <h3>3. Data-Driven Menu Engineering</h3>
            
            <p>Using analytics from their new systems, they identified their most profitable and popular dishes. This led to a streamlined menu that reduced inventory needs while highlighting customer favorites.</p>
            
            <h3>4. Targeted Marketing</h3>
            
            <p>With their new customer database, they launched personalized email campaigns with special offers based on previous orders. They also implemented a loyalty program that rewarded frequent customers.</p>
            
            <h3>5. Delivery Optimization</h3>
            
            <p>Rather than relying exclusively on third-party delivery services, they hired dedicated delivery staff for nearby orders and integrated multiple delivery platforms for wider reach.</p>
            
            <h2>The Results</h2>
            
            <p>Within 18 months of implementing these changes, Al-Madina Grill saw remarkable results:</p>
            
            <ul>
              <li>120% increase in overall revenue</li>
              <li>300% growth in online orders</li>
              <li>15% reduction in food waste</li>
              <li>40% increase in average order value</li>
              <li>Expansion to a second location</li>
            </ul>
            
            <blockquote>
              "Technology didn't change our food or our values—it just made them accessible to more people. Our father's recipes are still at the heart of everything we do." — Omar Haddad
            </blockquote>
            
            <h2>Lessons for Other Restaurants</h2>
            
            <p>The Al-Madina Grill story offers several valuable takeaways for other family-owned restaurants:</p>
            
            <ol>
              <li><strong>Start with the basics:</strong> A functional website and online ordering system provide the foundation for digital growth.</li>
              <li><strong>Integrate systems:</strong> Choose technology solutions that work together to reduce duplication and provide comprehensive data.</li>
              <li><strong>Embrace data:</strong> Use analytics to make informed decisions about menu offerings, pricing, and marketing.</li>
              <li><strong>Maintain authenticity:</strong> Technology should enhance, not replace, the unique character and cuisine that makes your restaurant special.</li>
              <li><strong>Incremental implementation:</strong> Phase in changes gradually to allow staff and customers to adapt.</li>
            </ol>
            
            <p>The transformation of Al-Madina Grill demonstrates that embracing technology doesn't mean abandoning tradition. Instead, digital tools can help beloved community restaurants reach new customers while preserving their unique culinary heritage.</p>
          `
                }
            ],
            regular: [
                {
                    id: 4,
                    title: "Understanding Halal Certification: A Restaurant Owner's Guide",
                    excerpt: "Everything restaurant owners need to know about the process, benefits, and marketing advantages of halal certification.",
                    category: "industry-news",
                    author: "Ahmed Hassan",
                    authorRole: "Halal Certification Consultant",
                    authorImage: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&w=100",
                    date: "February 1, 2025",
                    readTime: 7,
                    image: "https://images.unsplash.com/photo-1577219491135-ce391730fb2c?q=80&w=2070",
                    comments: 15,
                    content: `
            <p>For restaurant owners looking to expand their customer base and cater to the growing Muslim demographic, halal certification represents a significant opportunity. However, navigating the certification process can seem complex for those unfamiliar with the requirements.</p>
            
            <p>This comprehensive guide aims to demystify halal certification for restaurant owners, from understanding the basics to leveraging certification for business growth.</p>
            
            <h2>What Does Halal Certification Mean?</h2>
            
            <p>Halal certification verifies that your restaurant's ingredients, preparation methods, and overall operations comply with Islamic dietary laws. While most people associate halal with the absence of pork and alcohol, the requirements extend to:</p>
            
            <ul>
              <li>Sourcing meat from animals slaughtered according to Islamic guidelines</li>
              <li>Ensuring separation between halal and non-halal ingredients (if applicable)</li>
              <li>Verifying that ingredients don't contain prohibited additives or substances</li>
              <li>Maintaining proper sanitation and hygiene standards</li>
            </ul>
            
            <p>Certification is granted by recognized halal certifying bodies after thorough inspection and review.</p>
            
            <h2>The Certification Process</h2>
            
            <h3>1. Research and Selection</h3>
            
            <p>Begin by researching halal certifying organizations in your region. Look for bodies that are recognized internationally and by your local Muslim community. Popular certification bodies in the United States include:</p>
            
            <ul>
              <li>Islamic Food and Nutrition Council of America (IFANCA)</li>
              <li>Halal Food Council USA</li>
              <li>American Halal Foundation</li>
              <li>Islamic Services of America (ISA)</li>
            </ul>
            
            <h3>2. Application Submission</h3>
            
            <p>Most certifying bodies require detailed information about:</p>
            
            <ul>
              <li>Your menu and recipes</li>
              <li>Ingredient sources and suppliers</li>
              <li>Food preparation processes</li>
              <li>Facility layout</li>
              <li>Staff training procedures</li>
            </ul>
            
            <h3>3. Initial Assessment</h3>
            
            <p>Certifiers will review your application and may request additional documentation or clarification. They'll assess whether your restaurant can feasibly achieve certification or what changes would be necessary.</p>
            
            <h3>4. Facility Inspection</h3>
            
            <p>Inspectors will visit your restaurant to examine:</p>
            
            <ul>
              <li>Kitchen facilities and equipment</li>
              <li>Storage areas</li>
              <li>Preparation processes</li>
              <li>Ingredient labels and documentation</li>
              <li>Staff knowledge of halal requirements</li>
            </ul>
            
            <h3>5. Certification and Ongoing Compliance</h3>
            
            <p>Upon successful completion of the inspection, your restaurant will receive halal certification, typically valid for one year. Periodic re-inspections ensure continued compliance.</p>
            
            <h2>Common Challenges and Solutions</h2>
            
            <h3>Challenge: Sourcing Certified Halal Meat</h3>
            
            <p><strong>Solution:</strong> Develop relationships with certified halal meat suppliers in your area. Many national distributors now offer halal options due to increasing demand.</p>
            
            <h3>Challenge: Dual Menu Operations</h3>
            
            <p><strong>Solution:</strong> If you offer both halal and non-halal items, clear separation protocols are essential. This includes dedicated storage areas, preparation surfaces, utensils, and cooking equipment.</p>
            
            <h3>Challenge: Staff Training</h3>
            
            <p><strong>Solution:</strong> Develop comprehensive training materials and regular refresher courses. Ensure all staff understand the importance of halal compliance and proper procedures.</p>
            
            <h2>Business Benefits of Halal Certification</h2>
            
            <h3>1. Market Expansion</h3>
            
            <p>Certification opens your restaurant to Muslim customers who may otherwise not consider your establishment. This demographic represents a significant and growing market segment with considerable spending power.</p>
            
            <h3>2. Competitive Differentiation</h3>
            
            <p>In areas with limited halal dining options, certification can position your restaurant as a unique and inclusive choice, attracting not just Muslim customers but also their friends and colleagues.</p>
            
            <h3>3. Quality Perception</h3>
            
            <p>Many consumers associate halal certification with higher quality, cleanliness, and ethical standards, appealing to health-conscious and ethically-minded diners beyond the Muslim community.</p>
            
            <h3>4. Community Integration</h3>
            
            <p>Offering halal options signals inclusivity and cultural sensitivity, potentially strengthening your restaurant's relationship with diverse community segments.</p>
            
            <h2>Marketing Your Halal Certification</h2>
            
            <p>Once certified, consider these strategies to effectively promote your halal status:</p>
            
            <ul>
              <li>Display your certification prominently at your entrance and on your website</li>
              <li>Include the halal logo on menus and marketing materials</li>
              <li>List your restaurant on apps and websites specializing in halal dining options</li>
              <li>Engage with local Muslim community organizations and events</li>
              <li>Create special promotions around major Islamic holidays</li>
            </ul>
            
            <p>Halal certification represents a significant opportunity for restaurants to expand their customer base while demonstrating cultural awareness and inclusivity. Though the certification process requires investment and commitment, the potential business benefits make it worth considering for many restaurant owners.</p>
          `
                },
                {
                    id: 5,
                    title: "The Perfect Halal Food Tour of London",
                    excerpt: "A neighborhood-by-neighborhood guide to London's vibrant and diverse halal food scene.",
                    category: "travel",
                    author: "Zainab Mir",
                    authorRole: "Travel & Food Writer",
                    authorImage: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&w=100",
                    date: "January 25, 2025",
                    readTime: 9,
                    image: "https://images.unsplash.com/photo-1546412414-e1885e51cfa9?q=80&w=2148",
                    comments: 28,
                    content: `
            <p>London's culinary landscape is as diverse as its population, making it one of the world's most exciting cities for halal food enthusiasts. From traditional curry houses to innovative fusion restaurants, the city offers an incredible array of halal dining options across its many vibrant neighborhoods.</p>
            
            <p>This guide takes you on a curated food tour through London's most dynamic culinary districts, highlighting the best halal eateries in each area. Whether you're a visitor planning a trip or a local looking to explore new flavors, this neighborhood-by-neighborhood approach ensures you won't miss the city's most delicious halal offerings.</p>
            
            <h2>East London: Whitechapel and Brick Lane</h2>
            
            <p>No halal food tour of London would be complete without visiting the iconic Brick Lane area, the historic heart of the city's Bangladeshi community.</p>
            
            <h3>Must-Visit Restaurants:</h3>
            
            <ul>
              <li><strong>Tayyabs</strong> - This legendary Pakistani grill restaurant often has queues around the block for its sizzling lamb chops and tender kebabs. Booking ahead is essential.</li>
              <li><strong>Needoo Grill</strong> - A worthy alternative to Tayyabs offering exceptional Punjabi cuisine in a more relaxed atmosphere.</li>
              <li><strong>Gram Bangla</strong> - For authentic Bangladeshi home-style cooking that goes beyond the usual curry house menu.</li>
            </ul>
            
            <h3>Local Tip:</h3>
            
            <p>Visit Whitechapel Market to pick up traditional South Asian sweets and snacks from the various food stalls.</p>
            
            <h2>West London: Edgware Road</h2>
            
            <p>Often called "Little Arabia," Edgware Road is London's center for Middle Eastern cuisine, with many restaurants staying open late into the night.</p>
            
            <h3>Must-Visit Restaurants:</h3>
            
            <ul>
              <li><strong>Maroush</strong> - A London institution serving exceptional Lebanese cuisine across several branches.</li>
              <li><strong>Al Arez</strong> - Known for its generous portions and authentic Lebanese mezze.</li>
              <li><strong>Greenhill Café</strong> - A cozy spot offering some of the best shakshuka and Middle Eastern breakfast options in London.</li>
            </ul>
            
            <h3>Local Tip:</h3>
            
            <p>After dinner, experience the vibrant café culture with mint tea and shisha at one of the many cafés along the road (though keep in mind that shisha contains tobacco).</p>
            
            <h2>North London: Finsbury Park and Harringay Green Lanes</h2>
            
            <p>This area is home to a thriving Turkish and Kurdish community, offering some of the city's best grilled meats and Middle Eastern specialties.</p>
            
            <h3>Must-Visit Restaurants:</h3>
            
            <ul>
              <li><strong>Gökyüzü</strong> - Serving generous portions of authentic Turkish cuisine, their mixed grill platters are perfect for sharing.</li>
              <li><strong>Diyarbakir</strong> - Renowned for its lahmacun (Turkish flatbread) and pide (Turkish pizza).</li>
              <li><strong>Nandine</strong> - A family-run Kurdish restaurant offering unique dishes rarely found elsewhere in London.</li>
            </ul>
            
            <h3>Local Tip:</h3>
            
            <p>Visit the nearby Turkish supermarkets to pick up ingredients, baklava, and other Turkish delights to take home.</p>
            
            <h2>South London: Tooting</h2>
            
            <p>This increasingly trendy area boasts a thriving South Asian food scene with some of London's best-value curry houses and street food options.</p>
            
            <h3>Must-Visit Restaurants:</h3>
            
            <ul>
              <li><strong>Lahore Karahi</strong> - A no-frills Pakistani restaurant serving exceptional curries and grilled dishes.</li>
              <li><strong>Apollo Banana Leaf</strong> - Offering outstanding Sri Lankan cuisine at remarkably reasonable prices.</li>
              <li><strong>Dawat</strong> - Known for its authentic Mughlai cuisine and tandoori specialties.</li>
            </ul>
            
            <h3>Local Tip:</h3>
            
            <p>Explore Tooting Market and Broadway Market for a variety of halal street food stalls offering everything from Afghan mantu dumplings to Trinidadian doubles.</p>
            
            <h2>Central London: Soho and Fitzrovia</h2>
            
            <p>While central London offers numerous halal options, these neighborhoods stand out for their concentration of high-quality, trendy halal restaurants.</p>
            
            <h3>Must-Visit Restaurants:</h3>
            
            <ul>
              <li><strong>Berenjak</strong> - A stylish Persian restaurant inspired by the hole-in-the-wall kabab houses of Tehran.</li>
              <li><strong>Bintang</strong> - A Pan-Asian restaurant with Filipino influences and a fully halal menu.</li>
              <li><strong>The Great Chase</strong> - A rare find: a high-end halal restaurant with a sophisticated menu and alcohol-free environment.</li>
            </ul>
            
            <h3>Local Tip:</h3>
            
            <p>Many mainstream restaurants in this area offer halal options—always check ahead or ask your server.</p>
            
            <h2>East London: Stratford and Olympic Park</h2>
            
            <p>The 2012 Olympics transformed this area, which now offers diverse dining options reflecting East London's multicultural character.</p>
            
            <h3>Must-Visit Restaurants:</h3>
            
            <ul>
              <li><strong>The Meghna Grill</strong> - Serving outstanding Bengali cuisine in a relaxed setting.</li>
              <li><strong>Rhythm Kitchen</strong> - Offering authentic Jamaican dishes with a halal menu.</li>
              <li><strong>Cabana</strong> - Brazilian barbecue with halal chicken and lamb options.</li>
            </ul>
            
            <h3>Local Tip:</h3>
            
            <p>Visit Westfield Stratford City's food court for quick halal options from various global cuisines.</p>
            
            <h2>Planning Your London Halal Food Tour</h2>
            
            <h3>Transportation Tips:</h3>
            
            <p>London's excellent public transportation makes moving between these neighborhoods straightforward. Consider purchasing an Oyster card for unlimited daily travel, and use apps like Citymapper to navigate efficiently.</p>
            
            <h3>Timing Your Visit:</h3>
            
            <p>Many restaurants get extremely busy during evening hours, especially on weekends. For a more relaxed experience, consider late lunch visits when possible</p>
            <h3>Timing Your Visit:</h3>
            
            <p>Many restaurants get extremely busy during evening hours, especially on weekends. For a more relaxed experience, consider late lunch visits when possible or book ahead where restaurants accept reservations.</p>
            
            <h3>Ramadan Considerations:</h3>
            
            <p>If visiting during Ramadan, be aware that many restaurants offer special iftar menus and may have adjusted opening times. This can be an extraordinary time to experience the city's Muslim food culture at its most vibrant.</p>
            
            <h2>Beyond Restaurants: London's Halal Food Scene</h2>
            
            <h3>Food Markets:</h3>
            
            <ul>
              <li><strong>Borough Market</strong> - Several halal vendors can be found at this famous food market.</li>
              <li><strong>Spitalfields Market</strong> - Offers various halal street food options.</li>
              <li><strong>Boxpark Shoreditch</strong> - Features several halal-friendly vendors in a trendy container setting.</li>
            </ul>
            
            <h3>Sweet Treats:</h3>
            
            <p>Don't miss London's halal dessert scene, with specialty shops offering everything from artisanal gelato to creative doughnut concoctions. Highlights include:</p>
            
            <ul>
              <li><strong>Darlish</strong> - Persian-inspired ice cream and desserts</li>
              <li><strong>Knafeh Bakery</strong> - Authentic Middle Eastern desserts</li>
              <li><strong>Dum Dum Donutterie</strong> - Artisan baked (not fried) doughnuts</li>
            </ul>
            
            <p>London's halal food scene continues to evolve and expand, reflecting the city's status as a global culinary capital. Whether you're seeking traditional dishes or innovative fusion creations, you'll find that this diverse metropolis offers halal options to satisfy every palate and budget.</p>
          `
                },
                // Remaining blog post data...
            ]
        };
    };

    // Sample comments data
    const getComments = (postId) => {
        return [
            {
                id: 1,
                postId: postId,
                user: "Jamal Khan",
                date: "3 days ago",
                comment: "This article was incredibly informative! I've been looking for halal restaurants in my city and this gave me a better understanding of what to look for.",
                avatar: "https://images.unsplash.com/photo-1599566150163-29194dcaad36?ixlib=rb-4.0.3",
                likes: 5
            },
            {
                id: 2,
                postId: postId,
                user: "Aisha Mohammed",
                date: "5 days ago",
                comment: "I appreciate the depth of research that went into this article. Would love to see more content like this in the future!",
                avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3",
                likes: 3
            },
            {
                id: 3,
                postId: postId,
                user: "Ibrahim Patel",
                date: "1 week ago",
                comment: "As a restaurant owner considering halal certification, this was exactly what I needed to read. Thank you for the insights!",
                avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3",
                likes: 8
            }
        ];
    };

    // Function to get category name
    const getCategoryName = (categoryId) => {
        const categories = {
            'all': 'All Posts',
            'restaurant-spotlights': 'Restaurant Spotlights',
            'halal-guides': 'Halal Food Guides',
            'travel': 'Travel & Destinations',
            'industry-news': 'Industry News'
        };

        return categories[categoryId] || '';
    };

    // Function to get category icon
    const getCategoryIcon = (categoryId) => {
        switch (categoryId) {
            case 'restaurant-spotlights':
                return <Coffee size={18} />;
            case 'halal-guides':
                return <Tag size={18} />;
            case 'travel':
                return <Globe size={18} />;
            case 'industry-news':
                return <TrendingUp size={18} />;
            default:
                return <Tag size={18} />;
        }
    };

    const handleCommentSubmit = (e) => {
        e.preventDefault();

        if (!comment.trim()) return;

        // In a real app, this would send the comment to an API
        const newComment = {
            id: comments.length + 1,
            postId: post.id,
            user: "You",
            date: "Just now",
            comment: comment,
            avatar: "https://images.unsplash.com/photo-1633332755192-727a05c4013d?ixlib=rb-4.0.3",
            likes: 0
        };

        setComments([newComment, ...comments]);
        setComment('');
    };

    const handleShareClick = (platform) => {
        // In a real app, this would implement sharing functionality
        console.log(`Sharing to ${platform}`);

        // Example implementation
        const url = window.location.href;
        const title = post?.title;

        switch (platform) {
            case 'facebook':
                window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`);
                break;
            case 'twitter':
                window.open(`https://twitter.com/intent/tweet?text=${title}&url=${url}`);
                break;
            case 'linkedin':
                window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`);
                break;
            case 'email':
                window.open(`mailto:?subject=${title}&body=Check out this article: ${url}`);
                break;
            case 'copy':
                navigator.clipboard.writeText(url);
                setCopied(true);
                setTimeout(() => setCopied(false), 3000);
                break;
            default:
                break;
        }
    };

    if (isLoading) {
        return (
            <div className="container py-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading article...</p>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="container py-5 text-center">
                <h2>Article not found</h2>
                <p>The article you're looking for doesn't exist or has been removed.</p>
                <Link to="/blog" className="btn btn-primary">Back to Blog</Link>
            </div>
        );
    }

    return (
        <div className="blog-detail-page">
            {/* Article Header */}
            <div className="article-header">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            <Link to="/blog" className="back-link">
                                <ChevronLeft size={20} />
                                <span>Back to Blog</span>
                            </Link>

                            <div className="category-badge">
                                {getCategoryIcon(post.category)}
                                <span>{getCategoryName(post.category)}</span>
                            </div>

                            <h1 className="article-title">{post.title}</h1>

                            <div className="article-meta">
                                <div className="author-info">
                                    <img src={post.authorImage} alt={post.author} className="author-image" />
                                    <div>
                                        <h4 className="author-name">{post.author}</h4>
                                        <p className="author-role">{post.authorRole}</p>
                                    </div>
                                </div>

                                <div className="post-info">
                                    <div className="post-date">
                                        <Calendar size={16} />
                                        <span>{post.date}</span>
                                    </div>
                                    <div className="post-read-time">
                                        <Clock size={16} />
                                        <span>{post.readTime} min read</span>
                                    </div>
                                    <div className="post-comments">
                                        <MessageCircle size={16} />
                                        <span>{comments.length} comments</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Article Hero Image */}
            <div className="article-hero">
                <img src={post.image} alt={post.title} className="img-fluid" />
            </div>

            {/* Article Content */}
            <div className="article-content">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            {/* Article Text */}
                            <div className="content-text" dangerouslySetInnerHTML={{ __html: post.content }}></div>

                            {/* Article Tags */}
                            <div className="article-tags">
                                <h4>Related Topics</h4>
                                <div className="tags-container">
                                    <span className="tag">Halal Food</span>
                                    <span className="tag">{getCategoryName(post.category)}</span>
                                    <span className="tag">Restaurant Tips</span>
                                    <span className="tag">Food Culture</span>
                                </div>
                            </div>

                            {/* Article Actions */}
                            <div className="article-actions">
                                <div className="like-share">
                                    <button
                                        className={`like-btn ${liked ? 'liked' : ''}`}
                                        onClick={() => setLiked(!liked)}
                                    >
                                        <Heart size={20} fill={liked ? "#dc3545" : "none"} stroke={liked ? "#dc3545" : "currentColor"} />
                                        <span>{liked ? 'Liked' : 'Like'}</span>
                                    </button>

                                    <button
                                        className={`bookmark-btn ${bookmarked ? 'bookmarked' : ''}`}
                                        onClick={() => setBookmarked(!bookmarked)}
                                    >
                                        <Bookmark size={20} fill={bookmarked ? "#198754" : "none"} />
                                        <span>{bookmarked ? 'Saved' : 'Save'}</span>
                                    </button>
                                </div>

                                <div className="share-options">
                                    <span>Share:</span>
                                    <button className="share-btn facebook" onClick={() => handleShareClick('facebook')}>
                                        <Facebook size={18} />
                                    </button>
                                    <button className="share-btn twitter" onClick={() => handleShareClick('twitter')}>
                                        <Twitter size={18} />
                                    </button>
                                    <button className="share-btn linkedin" onClick={() => handleShareClick('linkedin')}>
                                        <Linkedin size={18} />
                                    </button>
                                    <button className="share-btn email" onClick={() => handleShareClick('email')}>
                                        <Mail size={18} />
                                    </button>
                                    <button className="share-btn copy" onClick={() => handleShareClick('copy')}>
                                        <Copy size={18} />
                                        {copied && <span className="copied-tooltip">Link copied!</span>}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Sidebar */}
                        <div className="col-lg-3 offset-lg-1">
                            {/* Author Card */}
                            <div className="author-card">
                                <img src={post.authorImage} alt={post.author} className="author-card-image" />
                                <h3 className="author-card-name">{post.author}</h3>
                                <p className="author-card-role">{post.authorRole}</p>
                                <p className="author-card-bio">
                                    {post.author} is a seasoned writer specializing in halal dining, restaurant trends, and food culture.
                                </p>
                                {/* <a href="#" className="btn btn-outline-primary btn-sm">View all articles</a> */}
                                <Link to="#" className="btn btn-outline-primary btn-sm">View all articles</Link>

                            </div>

                            {/* Table of Contents */}
                            <div className="toc-card">
                                <h4>Table of Contents</h4>
                                <ul className="toc-list">
                                    {post.content.match(/<h2>(.*?)<\/h2>/g)?.map((match, index) => {
                                        const title = match.replace(/<h2>|<\/h2>/g, '');
                                        const anchor = title.toLowerCase().replace(/\s+/g, '-');
                                        return (
                                            <li key={index}>
                                                <a href={`#${anchor}`}>{title}</a>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>

                          
                        </div>
                    </div>
                </div>
            </div>

            {/* Author Bio (Mobile) */}
            <div className="mobile-author-section d-lg-none">
                <div className="container">
                    <div className="mobile-author-card">
                        <img src={post.authorImage} alt={post.author} className="mobile-author-image" />
                        <div className="mobile-author-info">
                            <h3 className="mobile-author-name">{post.author}</h3>
                            <p className="mobile-author-role">{post.authorRole}</p>
                            <p className="mobile-author-bio">
                                {post.author} is a seasoned writer specializing in halal dining, restaurant trends, and food culture.
                            </p>
                            <Link to="/articles" className="btn btn-outline-primary btn-sm">View all articles</Link>
                            </div>
                    </div>
                </div>
            </div>

            {/* Comments Section */}
            <div className="comments-section">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            <h3 className="comments-title">Comments ({comments.length})</h3>

                            {/* Comment Form */}
                            <div className="comment-form-container">
                                <form onSubmit={handleCommentSubmit}>
                                    <div className="form-group">
                                        <textarea
                                            className="form-control"
                                            placeholder="Add a comment..."
                                            rows="3"
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                        ></textarea>
                                    </div>
                                    <button type="submit" className="btn btn-primary">
                                        Post Comment
                                    </button>
                                </form>
                            </div>

                            {/* Comments List */}
                            <div className="comments-list">
                                {comments.map((comment) => (
                                    <div key={comment.id} className="comment-card">
                                        <div className="comment-header">
                                            <div className="comment-user">
                                                <img src={comment.avatar} alt={comment.user} className="comment-avatar" />
                                                <div>
                                                    <h5 className="comment-username">{comment.user}</h5>
                                                    <p className="comment-date">{comment.date}</p>
                                                </div>
                                            </div>
                                            <button className="comment-like">
                                                <Heart size={14} />
                                                <span>{comment.likes}</span>
                                            </button>
                                        </div>
                                        <div className="comment-body">
                                            <p>{comment.comment}</p>
                                        </div>
                                        <div className="comment-actions">
                                            <button className="comment-reply-btn">Reply</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Related Articles */}
            {relatedPosts.length > 0 && (
                <div className="related-articles-section">
                    <div className="container">
                        <h2 className="section-title">Related Articles</h2>
                        <div className="row">
                            {relatedPosts.map(post => (
                                <div key={post.id} className="col-md-4">
                                    <div className="related-article-card">
                                        <div className="related-article-image">
                                            <img src={post.image} alt={post.title} />
                                            <div className="related-article-category">
                                                {getCategoryIcon(post.category)}
                                                <span>{getCategoryName(post.category)}</span>
                                            </div>
                                        </div>
                                        <div className="related-article-content">
                                            <h3 className="related-article-title">
                                                <Link to={`/blog/${post.id}`}>{post.title}</Link>
                                            </h3>
                                            <p className="related-article-excerpt">{post.excerpt}</p>
                                            <div className="related-article-meta">
                                                <div className="post-date">
                                                    <Calendar size={14} />
                                                    <span>{post.date}</span>
                                                </div>
                                                <div className="post-time">
                                                    <Clock size={14} />
                                                    <span>{post.readTime} min read</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Newsletter Section */}

            {/* Next/Previous Navigation */}
            <div className="article-navigation">
                <div className="container">
                    <div className="nav-buttons">
                        <Link to="/blog" className="nav-button prev">
                            <ChevronLeft size={20} />
                            <span>Previous Article</span>
                        </Link>
                        <Link to="/blog" className="nav-button next">
                            <span>Next Article</span>
                            <ChevronRight size={20} />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogDetail;